import {
  TextInput as AdminTextInput,
  ArrayInput,
  AutocompleteArrayInput,
  BooleanInput,
  Create,
  Datagrid,
  DeleteWithConfirmButton,
  Edit,
  EditButton,
  Filter,
  FormDataConsumer,
  List as RAList,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  TextInput,
  required,
  useRecordContext,
} from "react-admin";
import { Autocomplete, Box, Card, Chip, TextField as MuiTextField } from "@mui/material";
import { get, set } from "lodash";

import { GlobalListStyles } from "src/libs/globalListStyles";
import { ReferenceManyToManyInput } from "@react-admin/ra-relationships";
import { convertStringToNumber } from "../companies/companies";
import i18next from "src/locales";
import { useFormContext } from "react-hook-form";

const DefaultValueType = {
  DEFAULT_LOADING_LOCATION_ID: "Defaultní místo nakládky (ID)",
  DEFAULT_LOADING_CITY: "Defaultní město nakládky (Název)",
  DEFAULT_UNLOADING_LOCATION_ID: "Defaultní místo vykládky (ID)",
  DEFAULT_UNLOADING_CITY: "Defaultní město vykládky (Název)",
  LOADING_DATE: "Automatické přidání/odebrání dnu do datumu nakládky",
  UNLOADING_DATE: "Automatické přidání/odebrání dnu do datumu vykládky",
  ORDER_TYPE: "Typ zboží",
  CURRENCY: "Měna",
  PROFIT_CENTER: "Defaultní středisko",
  // ENABLED_DELIVERY_NOTE_HALF_PALLETS: "Povolen počet půl palet",
  // ENABLED_DELIVERY_NOTE_PACKAGES: "Povoleno počet balíků",
  // ENABLED_DELIVERY_NOTE_OVERSIZED_PACKAGES:
  //   "Povoleno počet nadrozměrných palet",
  ENABLED_DELIVERY_NOTE_LOADING_UNITS_VALIDATION:
    "Nastavit povinné zadávání vratných obalů",
  ENABLED_MULTIPLE_PROFIT_CENTERS: "Povoleno více středisek",
  ENABLED_MULTIPLE_ACTIONS: "Povoleno více akcí",
  ACTION: "Defaultní akce",
  AUTO_COPY_REFERENCE_NUMBER_TO_DELIVERY_NOTE:
    "Kopírovat referenční číslo do názvu dodacího listu (prvního vytvořeného)",
  AUTO_COPY_PALLET_PLACES_TO_SSCC:
    "Počet paletových míst kopírovat do počtu štítků (prvního vytvořeného)",
  AUTO_COPY_REFERENCE_NUMBER_TO_CODE:
    "Kopírovat referenční číslo do kódu nakládky",
  SPECIFIC_LOADING_UNITS: "Nastavení vratných obalů (multi)",
  DEFAULT_LOADING_UNIT: "Nastavení defaultní vratný obal",
  AUTO_COPY_SSCC_TO_LOADING_UNITS:
    "Kopírovat počet štítků do počtu vratných obalů",
  ENABLED_PREFIX_REFERENCE_NUMBER:
    "Povolit prefix pro referenční číslo (Datum ve formátu YYYY_DD_MM_HH_MM)",
  SPECIFIC_MULTIPLE_PROFIT_CENTERS:
    "Definice povolených středisek (multi)",
  ENABLED_SALES_TYPES:
    "Povolit možnost výberu ceníku",
};


export const Form = () => {
  const record = useRecordContext();
  const { setValue, watch } = useFormContext();

  return (
    <>
      <TextInput source="email" fullWidth />
      <TextInput source="firstName" fullWidth />
      <TextInput source="lastName" fullWidth />
      {!record?.id && (
        <TextInput source="password" fullWidth validate={[required()]} />
      )}

      <BooleanInput source="isAdmin" fullWidth />
      <BooleanInput source="isAd" fullWidth />
      <BooleanInput source="deliveryNotesVisitor" fullWidth />

      <TextInput source="ingresUsername" fullWidth />
      <TextInput source="carloUsername" fullWidth />

      <ReferenceManyToManyInput
        label="Companies"
        reference="Company"
        through="UserRelCompany"
        source="id"
        perPage={10000}
        using="user_id,company_id"
      >
        <AutocompleteArrayInput
          label="resources.User.fields.companies"
          translate="no"
          fullWidth
          optionText="name"
        />
      </ReferenceManyToManyInput>


      {record?.id && (
        <ArrayInput source="userDefaultValues" style={{ width: '100%' }} title={i18next.translate('resources.Company.fields.defaultValues')}>
          <SimpleFormIterator inline>
            <SelectInput
              source="property"
              label="Nastavení"
              style={{ width: 400 }}
              choices={Object.keys(DefaultValueType).map((id) => ({
                id,
                name: DefaultValueType[id],
              }))}
              required
            />
            <FormDataConsumer style={{ width: 500 }}>
              {({ scopedFormData, formData, getSource, ...rest }) => {
                const property = scopedFormData?.property;
                if (!property) return null;

                if (
                  [
                    "PROFIT_CENTER",
                    "DEFAULT_LOADING_LOCATION_ID",
                    "DEFAULT_LOADING_CITY",
                    "DEFAULT_LOADING_LOCATION_ID",
                    "DEFAULT_UNLOADING_LOCATION_ID",
                    "DEFAULT_UNLOADING_CITY",
                    "ORDER_TYPE",
                    "DEFAULT_LOADING_UNIT",
                    "ACTION",
                  ].includes(property)
                ) {
                  return (
                    <TextInput label="Hodnota" source={getSource("value")} required {...rest} />
                  );
                } else if (
                  ["LOADING_DATE", "UNLOADING_DATE"].includes(
                    property
                  )
                ) {
                  return (
                    <TextInput
                      defaultValue={0}
                      label="Hodnota"
                      parse={convertStringToNumber}
                      source={getSource("value")}
                      required
                      {...rest}
                    />
                  );
                } else if (["CURRENCY"].includes(property)) {
                  return (
                    <SelectInput
                      source={getSource("value")}
                      size="small"
                      label="Měna"
                      choices={[
                        {
                          id: "CZK",
                          name: "CZK",
                        },
                        {
                          id: "EUR",
                          name: "EUR",
                        },
                      ]}
                    />
                  );
                } else if (
                  [
                    "ENABLED_DELIVERY_NOTE_HALF_PALLETS",
                    "ENABLED_DELIVERY_NOTE_PACKAGES",
                    "ENABLED_DELIVERY_NOTE_OVERSIZED_PACKAGES",
                    "ENABLED_DELIVERY_NOTE_LOADING_UNITS_VALIDATION",
                    "ENABLED_MULTIPLE_PROFIT_CENTERS",
                    "ENABLED_MULTIPLE_ACTIONS",
                    "ENABLED_PREFIX_REFERENCE_NUMBER",
                    "AUTO_COPY_REFERENCE_NUMBER_TO_DELIVERY_NOTE",
                    "AUTO_COPY_PALLET_PLACES_TO_SSCC",
                    "AUTO_COPY_REFERENCE_NUMBER_TO_CODE",
                    "AUTO_COPY_SSCC_TO_LOADING_UNITS",
                    "ENABLED_SALES_TYPES",
                  ].includes(property)
                ) {
                  return (
                    <SelectInput
                      source={getSource("value")}
                      size="small"
                      label="Hodnota"
                      choices={[
                        {
                          id: "TRUE",
                          name: "Ano",
                        },
                        {
                          id: "FALSE",
                          name: "Ne",
                        },
                      ]}
                    />
                  );
                } else if (
                  ["SPECIFIC_LOADING_UNITS", "SPECIFIC_MULTIPLE_PROFIT_CENTERS"].includes(property)
                ) {
                  return (
                    <Autocomplete
                      multiple
                      freeSolo
                      style={{ width: 400 }}
                      renderTags={(value: readonly string[], getTagProps) =>
                        value.map((option: string, index: number) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              variant="outlined"
                              label={option}
                              key={key}
                              {...tagProps}
                            />
                          );
                        })
                      }
                      renderInput={(params) => (
                        <MuiTextField
                          {...params}
                          size="small"
                          variant="filled"
                          label="Values"
                          placeholder=""
                        />
                      )}
                      onChange={(_, values) =>
                        setValue(
                          "companyDefaultValues",
                          set(
                            formData,
                            getSource("value"),
                            JSON.stringify(values)
                          )[
                          "companyDefaultValues"
                          ]
                        )
                      }
                      options={[]}
                      defaultValue={JSON.parse(
                        get(formData, getSource("value")) || "[]"
                      )}
                    />
                  );
                }

                return null;
              }}
            </FormDataConsumer>
          </SimpleFormIterator>
        </ArrayInput>
      )}
    </>
  );
};

export const UserCreate = () => (
  <Create>
    <SimpleForm style={{ width: 400 }}>
      <Form />
    </SimpleForm>
  </Create>
);

export const UserEdit = () => (
  <Edit mutationMode="pessimistic">
    <SimpleForm style={{ width: 400 }}>
      <Form />
    </SimpleForm>
  </Edit>
);

const UserFilter = (props: any) => (
  <Filter {...props}>
    <AdminTextInput
      source="email"
      label={i18next.translate('resources.User.fields.searchEmail') as string}
      alwaysOn
      clearAlwaysVisible
    />
  </Filter>
);

export const UsersList = (props: any) => {
  return (
    <RAList
      {...props}
      filters={<UserFilter />}
      exporter={false}
      filter={{
        deletedAt: {
          equals: null
        },
      }}
      sx={{
        width: "98vw",
      }}
    >
      <GlobalListStyles />
      <Card sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 170px)",
        overflow: "hidden",
        width: "98vw",
      }}>
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <Datagrid sx={{
            minWidth: "900px",
            "& tr td": { paddingTop: 0, paddingBottom: 0 },
            "& .RaDatagrid-rowOdd": { backgroundColor: "#f0f0f0" },
            "& thead": {
              position: "sticky",
              top: 0,
              backgroundColor: "white",
              zIndex: 2,
            },
          }}>
            <TextField source="email" />
            <TextField source="firstName" />
            <TextField source="lastName" />
            <TextField source="phone" />
            <EditButton />
            <DeleteWithConfirmButton />
          </Datagrid>
        </Box>
      </Card>
    </RAList>
  );
};
